const COMMON_FIRST_NAMES = new Set(['JOSE', 'MA', 'MARIA']);

const IGNORED_WORDS = new Set([
  'DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y',
  'MC', 'MAC', 'VAN', 'VON', 'DA', 'DI', 'DO', 'DU'
]);

const CURP_STATE_CODES = new Set([
  'AS', 'BC', 'BS', 'CC', 'CL', 'CM', 'CS', 'CH', 'DF', 'DG', 'GT', 'GR', 'HG',
  'JC', 'MC', 'MN', 'MS', 'NT', 'NL', 'OC', 'PL', 'QT', 'QR', 'SP', 'SL', 'SR',
  'TC', 'TS', 'TL', 'VZ', 'YN', 'ZS', 'NE'
]);

function normalizeWord(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/Ñ/g, 'N')
    .replace(/[^A-Z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanNameTokens(value) {
  const tokens = normalizeWord(value).split(' ').filter(Boolean);
  const filtered = tokens.filter((token) => !IGNORED_WORDS.has(token));
  return filtered.length ? filtered : tokens;
}

function firstInternalVowel(text) {
  for (let i = 1; i < text.length; i += 1) {
    const c = text[i];
    if (/[AEIOU]/.test(c)) return c;
  }
  return '';
}

function inePair(rawValue) {
  if (!rawValue) return 'XX';

  const original = String(rawValue).toUpperCase().trim();
  const words = original.split(/\s+/).filter(Boolean);
  const filtered = words.filter((word) => !IGNORED_WORDS.has(word));
  const cleaned = (filtered.length ? filtered.join(' ') : words.join(' ')).replace(/[^A-ZÑ]/g, '');

  if (!cleaned) return 'XX';

  const first = cleaned.charAt(0);
  const rest = cleaned.slice(1);
  const consonant = rest.split('').find((char) => !'AEIOUÑ'.includes(char));

  return (first + (consonant || rest.charAt(0) || 'X')).replace(/Ñ/g, 'N');
}

function getNamePairs(name) {
  const words = cleanNameTokens(name);
  if (!words.length) return ['XX'];

  const first = words[0];
  const isAmbiguous = first.length === 1 || COMMON_FIRST_NAMES.has(first);

  if (isAmbiguous) {
    const results = new Set();
    const second = words[1] || '';

    results.add(first.charAt(0) + 'X');
    if (first.length === 2) results.add(first);
    results.add(inePair(first));

    if (second) {
      const clean = normalizeWord(second).replace(/[^A-Z]/g, '');
      const consonant = clean.split('').find((char) => !'AEIOU'.includes(char));
      const vowel = clean.split('').find((char) => 'AEIOU'.includes(char));

      if (consonant) results.add(first.charAt(0) + consonant);
      if (vowel) results.add(first.charAt(0) + vowel);
      results.add(inePair(second));
    }

    return Array.from(results);
  }

  return [inePair(first)];
}

function generateAllINEKeys(firstSurname, secondSurname, name) {
  const p1 = inePair(firstSurname);
  const p2 = secondSurname ? inePair(secondSurname) : 'XX';
  const namePairs = getNamePairs(name);

  return namePairs.map((p3) => (p1 + p2 + p3).substring(0, 6));
}

function parseDateYYMMDD(value) {
  if (!/^\d{6}$/.test(value)) return null;
  const yy = Number(value.slice(0, 2));
  const mm = Number(value.slice(2, 4));
  const dd = Number(value.slice(4, 6));
  const currentYY = new Date().getFullYear() % 100;
  const year = yy > currentYY ? 1900 + yy : 2000 + yy;
  const date = new Date(Date.UTC(year, mm - 1, dd));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== mm - 1 ||
    date.getUTCDate() !== dd
  ) return null;
  return date;
}

export function validateIneKeyStructure(rawKey) {
  const key = String(rawKey || '').trim().toUpperCase();
  if (key.length !== 18) return false;
  const base = /^([A-Z]{6})(\d{6})(\d{2})(H|M)(\d{3})$/i.exec(key);
  if (!base) return false;
  if (!parseDateYYMMDD(key.slice(6, 12))) return false;
  if (!/[HM]/.test(key[14])) return false;
  return true;
}

export function parseIneMetadata(rawKey) {
  const key = String(rawKey || '').trim().toUpperCase();
  const birthDate = parseDateYYMMDD(key.slice(6, 12));
  const sex = /[HM]/.test(key[14]) ? key[14] : null;
  return {
    birthDate: birthDate ? `${birthDate.getUTCFullYear()}-${String(birthDate.getUTCMonth() + 1).padStart(2, '0')}-${String(birthDate.getUTCDate()).padStart(2, '0')}` : null,
    sex,
  };
}

export function validateIneNameMatch(rawIneKey, firstName, lastNamePaternal, lastNameMaternal) {
  const key = String(rawIneKey || '').trim().toUpperCase();
  if (key.length < 6) return null;
  const prefix = key.substring(0, 6);
  const validKeys = generateAllINEKeys(lastNamePaternal, lastNameMaternal, firstName);
  return validKeys.includes(prefix);
}

function curpRegex() {
  return /^([A-Z][AEIOUX][A-Z]{2})(\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01]))([HM])(AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)([B-DF-HJ-NP-TV-Z]{3})([0-9A-Z])(\d)$/;
}

function curpPrimaryNameToken(name) {
  const tokens = cleanNameTokens(name);
  if (!tokens.length) return '';
  if (tokens.length >= 2 && COMMON_FIRST_NAMES.has(tokens[0])) return tokens[1];
  return tokens[0];
}

function expectedCurpInitials(data) {
  const paternal = cleanNameTokens(data.firstLastName || '').join('');
  const maternal = cleanNameTokens(data.secondLastName || '').join('');
  const nameToken = curpPrimaryNameToken(data.name || '');
  return {
    first: paternal[0] || 'X',
    second: firstInternalVowel(paternal) || 'X',
    third: maternal[0] || 'X',
    fourth: nameToken[0] || 'X',
  };
}

function parseFlexibleDate(input) {
  if (!input) return null;
  const text = String(input).trim();
  let year; let month; let day;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    year = Number(text.slice(0, 4));
    month = Number(text.slice(5, 7));
    day = Number(text.slice(8, 10));
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(text)) {
    day = Number(text.slice(0, 2));
    month = Number(text.slice(3, 5));
    year = Number(text.slice(6, 10));
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
    day = Number(text.slice(0, 2));
    month = Number(text.slice(3, 5));
    year = Number(text.slice(6, 10));
  } else {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) return null;
  return `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
}

export function validateCurp(curpRaw, payload) {
  const curp = String(curpRaw || '').trim().toUpperCase();
  if (!curp) return { valid: false, reason: 'La CURP es obligatoria.' };
  if (!curpRegex().test(curp)) {
    return { valid: false, reason: 'La CURP no cumple con la estructura oficial.' };
  }

  const initials = expectedCurpInitials(payload || {});
  const c0 = curp[0];
  const c1 = curp[1];
  const c2 = curp[2];
  const c3 = curp[3];

  if (c0 !== initials.first && c0 !== 'X') {
    return { valid: false, reason: 'La CURP no coincide con el apellido paterno.' };
  }
  if (c1 !== initials.second && c1 !== 'X') {
    return { valid: false, reason: 'La CURP no coincide con el apellido paterno.' };
  }
  if (!(c2 === initials.third || c2 === 'X' || initials.third === 'X')) {
    return { valid: false, reason: 'La CURP no coincide con el apellido materno.' };
  }
  if (!(c3 === initials.fourth || c3 === 'X')) {
    return { valid: false, reason: 'La CURP no coincide con el nombre.' };
  }

  if (payload?.birthDate) {
    const birth = parseFlexibleDate(payload.birthDate);
    if (!birth) return { valid: false, reason: 'La fecha de nacimiento es invalida.' };
    if (curp.slice(4, 10) !== birth.slice(2, 8)) {
      return { valid: false, reason: 'La fecha de nacimiento no coincide con la CURP.' };
    }
  }

  if (payload?.sex) {
    const sex = String(payload.sex).trim().toUpperCase();
    if (!['H', 'M'].includes(sex)) {
      return { valid: false, reason: 'El sexo debe ser H o M.' };
    }
    if (curp[10] !== sex) {
      return { valid: false, reason: 'El sexo no coincide con la CURP.' };
    }
  }

  if (payload?.state) {
    const state = String(payload.state).trim().toUpperCase();
    if (state.length === 2 && CURP_STATE_CODES.has(state) && curp.slice(11, 13) !== state) {
      return { valid: false, reason: 'La entidad federativa no coincide con la CURP.' };
    }
  }

  return { valid: true };
}
